import { useEffect, useState, type FormEvent } from "react";
import type { PanelUser } from "../types";
import { api } from "../api";

type ClStatus = {
  detected?: boolean;
  mode?: string;
  prettyName?: string;
  supportTier?: string;
  cagefsInstalled?: boolean;
  lveInstalled?: boolean;
  phpSelectorInstalled?: boolean;
  clnLicensed?: boolean | null;
  notes?: string[];
  tools?: Record<string, boolean>;
  defaults?: {
    cpu: number;
    pmemMb: number;
    io: number;
    nproc: number;
    ep: number;
  };
};

type VerifyResult = {
  owner: string;
  ok: boolean;
  checks: { name: string; status: string; detail?: string }[];
  notes: string[];
};

export function IsolationPage({
  accounts,
  onRefresh
}: {
  accounts: PanelUser[];
  onRefresh: () => Promise<void>;
}) {
  const users = accounts.filter((a) => a.role === "user");
  const [status, setStatus] = useState<ClStatus | null>(null);
  const [userId, setUserId] = useState(users[0]?.id || "");
  const [cpu, setCpu] = useState(100);
  const [pmemMb, setPmemMb] = useState(1024);
  const [io, setIo] = useState(1024);
  const [nproc, setNproc] = useState(100);
  const [ep, setEp] = useState(20);
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [selector, setSelector] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const res = await api<{ ok: boolean; status: ClStatus }>(
        "/api/v1/isolation/status"
      );
      setStatus(res.status);
      if (res.status?.defaults) {
        setCpu(res.status.defaults.cpu);
        setPmemMb(res.status.defaults.pmemMb);
        setIo(res.status.defaults.io);
        setNproc(res.status.defaults.nproc);
        setEp(res.status.defaults.ep);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const loadLve = async (id: string) => {
    if (!id) return;
    try {
      const res = await api<{
        ok: boolean;
        limits?: {
          cpu: number;
          pmemMb: number;
          io: number;
          nproc: number;
          ep: number;
        };
      }>(`/api/v1/isolation/lve/${id}`);
      if (res.limits) {
        setCpu(res.limits.cpu);
        setPmemMb(res.limits.pmemMb);
        setIo(res.limits.io);
        setNproc(res.limits.nproc);
        setEp(res.limits.ep);
      }
    } catch {
      /* */
    }
  };

  useEffect(() => {
    void loadStatus();
  }, []);

  useEffect(() => {
    if (userId) void loadLve(userId);
  }, [userId]);

  const onSaveLve = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{
        ok: boolean;
        agentOk?: boolean;
        agentError?: string | null;
      }>("/api/v1/isolation/lve", {
        method: "POST",
        body: JSON.stringify({ userId, cpu, pmemMb, io, nproc, ep })
      });
      setMsg(
        res.agentOk === false
          ? `Saved (agent note: ${res.agentError || "non-native"})`
          : "LVE limits saved"
      );
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const cagefs = async (enabled: boolean) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/isolation/cagefs", {
        method: "POST",
        body: JSON.stringify({ userId, enabled })
      });
      setMsg(enabled ? "CageFS enabled" : "CageFS disabled");
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const runVerify = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api<{ ok: boolean; result: VerifyResult }>(
        "/api/v1/isolation/verify",
        { method: "POST", body: JSON.stringify({ userId }) }
      );
      setVerify(res.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const loadSelector = async () => {
    try {
      const res = await api<{ ok: boolean; selector: Record<string, unknown> }>(
        `/api/v1/isolation/php-selector?userId=${encodeURIComponent(userId)}`
      );
      setSelector(res.selector);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const mode = status?.mode || "—";
  const isCl = Boolean(status?.detected);

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Resource Limits</h1>
          <p>
            Advanced · per-account CPU, memory, I/O, and process limits (CloudLinux
            LVE / CageFS when available). On non-CL hosts, limits are stored and
            reported in shadow mode.
          </p>
        </div>
        <div className="topbar-actions">
          <button type="button" className="btn btn-ghost" onClick={() => void loadStatus()}>
            Refresh status
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      {msg ? (
        <div className="chip" style={{ marginBottom: 12 }}>
          {msg}
        </div>
      ) : null}

      <section className="card" style={{ marginBottom: 16 }}>
        <div className="card-label">Host capability</div>
        <dl className="kv compact">
          <dt>CloudLinux</dt>
          <dd>{isCl ? "yes" : "no"}</dd>
          <dt>mode</dt>
          <dd className="mono">{mode}</dd>
          <dt>OS</dt>
          <dd className="mono">{status?.prettyName || "—"}</dd>
          <dt>tier</dt>
          <dd>{status?.supportTier || "—"}</dd>
          <dt>CageFS</dt>
          <dd>{status?.cagefsInstalled ? "installed" : "no"}</dd>
          <dt>LVE</dt>
          <dd>{status?.lveInstalled ? "installed" : "no"}</dd>
          <dt>PHP Selector</dt>
          <dd>{status?.phpSelectorInstalled ? "installed" : "no"}</dd>
          <dt>CLN license</dt>
          <dd>
            {status?.clnLicensed === null
              ? "unknown"
              : status?.clnLicensed
                ? "active"
                : "inactive / unknown"}
          </dd>
        </dl>
        {status?.notes?.map((n) => (
          <p key={n} className="card-meta">
            {n}
          </p>
        ))}
        {!isCl && mode !== "native" ? (
          <p className="card-meta" style={{ marginTop: 8 }}>
            This server is not CloudLinux. Dense shared-hosting isolation
            requires CL 8/9 + license. See <code>docs/cloudlinux.md</code>.
          </p>
        ) : null}
      </section>

      <form className="panel-form" onSubmit={onSaveLve}>
        <div className="panel-form-title">Per-account LVE limits</div>
        <div className="form-grid">
          <div className="field">
            <label>Account</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)}>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}
                  {u.cagefsEnabled ? " · cagefs" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>CPU % (SPEED)</label>
            <input
              type="number"
              min={1}
              value={cpu}
              onChange={(e) => setCpu(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label>PMEM (MB)</label>
            <input
              type="number"
              min={64}
              value={pmemMb}
              onChange={(e) => setPmemMb(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label>I/O</label>
            <input
              type="number"
              min={0}
              value={io}
              onChange={(e) => setIo(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label>NPROC</label>
            <input
              type="number"
              min={1}
              value={nproc}
              onChange={(e) => setNproc(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label>Entry processes (EP)</label>
            <input
              type="number"
              min={1}
              value={ep}
              onChange={(e) => setEp(Number(e.target.value))}
            />
          </div>
        </div>
        <div className="row-actions">
          <button className="btn btn-primary btn-inline" disabled={busy || !userId} type="submit">
            Save LVE
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-inline"
            disabled={busy || !userId}
            onClick={() => void cagefs(true)}
          >
            Enable CageFS
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-inline"
            disabled={busy || !userId}
            onClick={() => void cagefs(false)}
          >
            Disable CageFS
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-inline"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api("/api/v1/isolation/cagefs/rebuild", {
                  method: "POST",
                  body: JSON.stringify({ userId: userId || null })
                });
                setMsg("CageFS rebuild requested");
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            Rebuild CageFS
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-inline"
            disabled={busy || !userId}
            onClick={() => void runVerify()}
          >
            Verify isolation
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-inline"
            disabled={!userId}
            onClick={() => void loadSelector()}
          >
            PHP Selector
          </button>
        </div>
      </form>

      {selector ? (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="card-label">PHP Selector</div>
          <pre className="mono" style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(selector, null, 2)}
          </pre>
        </section>
      ) : null}

      {verify ? (
        <section className="card">
          <div className="card-label">
            Isolation verify — {verify.owner}{" "}
            <span className={`badge ${verify.ok ? "primary" : "status-suspended"}`}>
              {verify.ok ? "ok" : "issues"}
            </span>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Check</th>
                <th>Status</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {verify.checks.map((c) => (
                <tr key={c.name}>
                  <td className="mono">{c.name}</td>
                  <td>{c.status}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {c.detail || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {verify.notes?.map((n) => (
            <p key={n} className="card-meta">
              {n}
            </p>
          ))}
        </section>
      ) : null}
    </>
  );
}
